import React, { useEffect, useState, useRef, useContext } from 'react';
import { AuthContext } from '../context/AuthContext'; // Ensure correct import
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom'; // Add useLocation and useNavigate
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faCheckCircle, faBookmark, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'; // Import Leaflet components
import L from 'leaflet';
import './DealerDetails.css';
import Config from '../utils/Config'; // Import the Config file
import {
  formatDate,
  formatPhoneNumber,
  calculateExpirationDate,
  getIRSRegion,
  isDateExpired,
  fetchFromAPI,
  getDaysLeft,
  getExpirationClass
} from '../utils/functions'; // Import utility functions
import useTitle from '../hooks/useTitle';
import { toast } from 'react-toastify';

const displayDealerIcon = new L.Icon({
  iconUrl: `${window.location.origin}/images/marker-icon-red.png`, // Replace with your custom icon URL
  iconSize: [25, 41], // Standard Leaflet icon size
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  shadowSize: [41, 41],
});

const nearbyDealerIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png', // Default Leaflet marker
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  shadowSize: [41, 41],
});

const DealerDetails = () => {
  let { id, subdomain } = useParams();
  const [dealer, setDealer] = useState(null);
  const [typeDescription, setTypeDescription] = useState(null);
  const [otherFFLs, setOtherFFLs] = useState([]);
  const [position, setPosition] = useState(null);
  const [localTime, setLocalTime] = useState(null);
  const [otherLocations, setOtherLocations] = useState([]);
  const [popupOpened, setPopupOpened] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const markerRef = useRef(); // Define the markerRef
  const [isSaved, setIsSaved] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);

  useTitle(dealer?.BUSINESS_NAME || dealer?.LICENSE_NAME || 'Dealer Details');

  const { isAuthenticated, user } = useContext(AuthContext); // Ensure AuthContext is accessible
  // Use useLocation and useNavigate hooks
  const location = useLocation();
  const navigate = useNavigate();
  const originRoute = location.state?.origin || '/'; // Default to home if no origin is provided

  // inside DealerDetails render (before return)
  const hasApprovedClaim = dealer?.hasApprovedClaim === true;
  const administrator = isAuthenticated && (isAdmin || Number(user?.userLevel) >= 3 || [
    'Super Admin', 'Operations Admin', 'Support Admin', 'Billing Admin', 'Content Admin'
  ].includes(user?.adminRole));
  const claimDisabled = hasApprovedClaim;
  const claimLabel = hasApprovedClaim ? 'Already Claimed' : 'Claim this FFL';
  const claimTitle = hasApprovedClaim
    ? 'This FFL is already claimed by its owner'
    : 'Is this your FFL?';

  // small utility to build a stable ID string
  const fflIdString = (d) =>
    `${d.LIC_REGN}-${d.LIC_DIST}-${d.LIC_CNTY}-${d.LIC_TYPE}-${d.LIC_XPRDTE}-${d.LIC_SEQN}`;

  // fetch saved status for this dealer
  useEffect(() => {
    const checkSaved = async () => {
      if (!isAuthenticated || !id) { setIsSaved(false); return; }
      try {
        const res = await fetchFromAPI(Config.ENDPOINTS.SAVED_DEALERS.STATUS(id), {
          method: 'GET',
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
        });
        setIsSaved(!!res?.data?.saved);
      } catch (_) {
        // stay quiet; not fatal
        setIsSaved(false);
      }
    };
    checkSaved();
  }, [isAuthenticated, id]);

  const handleClaimClick = () => {
    const fflNumber = `${dealer.LIC_REGN}-${dealer.LIC_DIST}-${dealer.LIC_CNTY}-${dealer.LIC_TYPE}-${dealer.LIC_XPRDTE}-${dealer.LIC_SEQN}`;
    const fflName = dealer.LICENSE_NAME || "N/A";
    const businessName = dealer.BUSINESS_NAME || "N/A";

    if (hasApprovedClaim) return;
    if (administrator) {
      toast.info('Administrator accounts cannot submit personal dealership claims. Use an ordinary dealership owner account to claim your FFL.');
      return;
    }

    if (!isAuthenticated) {
      localStorage.setItem("postLoginRedirect", JSON.stringify({
        fflNumber,
        fflName,
        businessName
      }));

      localStorage.setItem("postLoginRedirectPath", location.pathname);
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    localStorage.setItem("dealerId", id);
    localStorage.removeItem("claimId");
    localStorage.setItem("activeTab", 'claim-ffl');

    navigate('/dashboard', {
      state: { fflNumber, fflName, businessName },
    });
  };

  useEffect(() => {
    if (markerRef.current && position) {
      setTimeout(() => {
        markerRef.current.openPopup(); // Ensure the popup opens after the marker is fully rendered
      }, 100); // Adjust the timeout as needed
    }
  }, [markerRef, position]);

  useEffect(() => {
    const fetchDealerDetails = async () => {
      try {
        const response = await fetchFromAPI(`${Config.ENDPOINTS.DEALERS_DETAILS}/${id}`, {
          method: 'GET'
        });
        setDealer(response.data);

        if (response.data.LIC_TYPE) {
          // Fetch the type description from the backend
          const typeResponse = await fetchFromAPI(`${Config.ENDPOINTS.DEALERS_TYPES}/${response.data.LIC_TYPE}`, {
            method: 'GET'
          });
          setTypeDescription(typeResponse.data.definition);
        }

        const locationResponse = await fetchFromAPI(`${Config.ENDPOINTS.DEALERS_FFL_LOCATIONS}/${id}`, {
          method: 'GET'
        });
        if (locationResponse.data) {
          const { latitude, longitude } = locationResponse.data;
          if (latitude && longitude) {
            setPosition([parseFloat(latitude), parseFloat(longitude)]);
            const timezoneResponse = await axios.get(
              `https://api.timezonedb.com/v2.1/get-time-zone`,
              {
                params: {
                  key: `${Config.API.TIME_ZONE_DB_KEY}`,
                  format: 'json',
                  by: 'position',
                  lat: latitude,
                  lng: longitude,
                },
              }
            );
            if (timezoneResponse.data) {
              const rawTime = new Date(timezoneResponse.data.formatted);
              const formattedTime = rawTime.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true, // Use 12-hour clock format
              });
              const timeWithAbbreviation = `${formattedTime} (${timezoneResponse.data.abbreviation})`;
              setLocalTime(timeWithAbbreviation);
            }
          }
        } else {
          // console.warn('Location not found for this dealer.');
        }

        // Fetch other FFLs in the same ZIP code
        if (response.data.PREMISE_ADDRESS?.ZIP_CODE) {

          const zipResponse = await fetchFromAPI(`${Config.ENDPOINTS.DEALERS_ZIPCODE}/${response.data.PREMISE_ADDRESS.ZIP_CODE}`, {
            method: 'GET'
          });

          const filteredFFLs = zipResponse.data.filter((ffl) => ffl._id !== id);
          setOtherFFLs(filteredFFLs);

          // Fetch locations for other FFLs
          const locationPromises = filteredFFLs.map((ffl) =>
            fetchFromAPI(`${Config.ENDPOINTS.DEALERS_FFL_LOCATIONS}/${ffl._id}`, {
              method: 'GET',
            })
          );
          const locations = await Promise.all(locationPromises);
          setOtherLocations(
            locations
              .filter((loc) => loc.data) // Filter out undefined locations
              .map((loc) => ({
                id: loc.data._id,
                latitude: loc.data.latitude,
                longitude: loc.data.longitude,
                name: loc.data.BUSINESS_NAME || loc.data.LICENSE_NAME || 'N/A',
                address: loc.data.PREMISE_ADDRESS,
              }))
          );
        }
      } catch (error) {
        // console.error('Error fetching dealer details or type description:', error);
      }
    };

    fetchDealerDetails();
  }, [id]);

  useEffect(() => {
    const fetchUserLevel = async () => {
      try {
        const response = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USERS_IS_ADMIN}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("authToken")}`
          },
        });

        const data = await response.json();
        if (data.isadmin) {
          setIsAdmin(true);
        }
      } catch (error) {
        // console.error('Error fetching dealer details or type description:', error);
      }
    };

    fetchUserLevel();
  }, [id]);

  useEffect(() => {
    // Ensure markerRef and position are ready before opening the popup
    if (markerRef.current && position && !popupOpened) {
      setTimeout(() => {
        markerRef.current.openPopup();
        setPopupOpened(true); // Ensure the popup is not opened repeatedly
      }, 100); // Delay to ensure marker rendering
    }
  }, [position, popupOpened]);

  if (!dealer) return <p>Loading dealer details...</p>;

  const { PREMISE_ADDRESS } = dealer;

  // Get IRS Region description
  const irsRegionDescription = getIRSRegion(dealer.LIC_REGN);
  const expirationDate = calculateExpirationDate(dealer.LIC_XPRDTE);

  const handleToggleSave = async () => {
    if (!dealer) return;

    if (!isAuthenticated) {
      // bounce to login and return here after
      localStorage.setItem("postLoginRedirectPath", location.pathname);
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    if (saveBusy) return;

    setSaveBusy(true);
    const next = !isSaved;
    setIsSaved(next); // optimistic

    try {
      if (next) {
        await fetchFromAPI(`${Config.ENDPOINTS.SAVED_DEALERS.LIST}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({ dealerId: id, fflNumber: fflIdString(dealer) })
        });
        // toast.success('Dealer saved'); // optional
      } else {
        await fetchFromAPI(Config.ENDPOINTS.SAVED_DEALERS.UNSAVE(id), {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
        });
        // toast.info('Dealer removed'); // optional
      }
    } catch (err) {
      // revert on failure
      setIsSaved(!next);
      toast.error(err.message || 'Could not update saved dealer. Try again.');
    } finally {
      setSaveBusy(false);
    }
  }

  return (
    <div className="dealer-details">
      <nav className="dealer-nav">
        <Link to={originRoute} className="back-link">
          <FontAwesomeIcon icon={faChevronLeft} /> Home
        </Link>

        <div className="dealer-nav-actions">
          <button
            className={`save-dealer-button ${isSaved ? 'saved' : ''}`}
            onClick={handleToggleSave}
            title={isSaved ? 'Remove bookmark' : 'Save this dealer'}
          >
            <FontAwesomeIcon icon={faBookmark} />
          </button>
          <button
            className={`claim-button ${claimDisabled ? 'is-disabled' : ''}`}
            onClick={claimDisabled ? undefined : handleClaimClick}
            disabled={claimDisabled}
            title={claimTitle}
          >
            {claimLabel}
          </button>
        </div>
        
      </nav>
      <header className="dealer-header">
        <h1>
          {dealer.BUSINESS_NAME || dealer.LICENSE_NAME || 'Dealer Details'} {dealer.LIC_SEQN}
        </h1>
        <div>
          {dealer.VERIFIED && (
            <span className="verified-badge">
              <FontAwesomeIcon icon={faCheckCircle} className="verified-icon" />
              Verified Dealer
            </span>
          )}
        </div>
        <p className="dealer-note">
          {Config.APP.URL} is providing you with the following links to identify Federal Firearms License numbers both expired and current. This does not relieve you of the requirement to obtain a copy of the FFL from the licensee if you are shipping a firearm to the receiver before you transfer the firearm. 27 CFR 478.94.
        </p>
      </header>
      <section className="dealer-overview">
        <h2>Overview</h2>
        <p>{dealer.BUSINESS_NAME || dealer.LICENSE_NAME} is licensed by the Bureau of Alcohol, Tobacco, Firearms, and Explosives (ATF).</p>
        <p>
          The Federal Firearms License (FFL) number is{' '}
          <span className="overview-highlight">
            {dealer.LIC_REGN}-{dealer.LIC_DIST}-{dealer.LIC_CNTY}-{dealer.LIC_TYPE}-{dealer.LIC_XPRDTE}-{dealer.LIC_SEQN}
          </span>.
        </p>
        <p>
          The business address is{' '}
          <span className="overview-highlight">
            {dealer.PREMISE_ADDRESS?.STREET || 'N/A'}, {dealer.PREMISE_ADDRESS?.CITY || 'N/A'}, {dealer.PREMISE_ADDRESS?.STATE || 'N/A'} {dealer.PREMISE_ADDRESS?.ZIP_CODE || 'N/A'}
          </span>.
        </p>
        <p>
          The Federal Firearms License (FFL) expires on{' '}
          <span className={`claim-date ${getExpirationClass(expirationDate)}`}>
            {formatDate(expirationDate)}&nbsp;&nbsp;({getDaysLeft(expirationDate)} days left)
          </span>
        </p>
      </section>
      <div className="details-container">
        <div className="details-box">
          <h2>FFL Overview</h2>
          <p><strong>License Number:</strong> {dealer.LIC_REGN}-{dealer.LIC_DIST}-{dealer.LIC_CNTY}-{dealer.LIC_TYPE}-{dealer.LIC_XPRDTE}-{dealer.LIC_SEQN}</p>
          <p><strong>License Name:</strong> {dealer.LICENSE_NAME}</p>
          <p><strong>Business Name:</strong> {dealer.BUSINESS_NAME || 'N/A'}</p>
          <p><strong>Expires:</strong>&nbsp;
            <span className={`claim-date ${getExpirationClass(expirationDate)}`}>
              {formatDate(expirationDate)}&nbsp;&nbsp;({getDaysLeft(expirationDate)} days left)
            </span>
          </p>
          <hr className="thin-divider" />
          <p><strong>Address:</strong> {dealer.PREMISE_ADDRESS?.STREET || 'N/A'}, {dealer.PREMISE_ADDRESS?.CITY || 'N/A'}, {dealer.PREMISE_ADDRESS?.STATE || 'N/A'} {dealer.PREMISE_ADDRESS?.ZIP_CODE || 'N/A'}</p>
          <p><strong>Mailing Address:</strong> {dealer.MAILING_ADDRESS?.STREET || 'N/A'}, {dealer.MAILING_ADDRESS?.CITY || 'N/A'}, {dealer.MAILING_ADDRESS?.STATE || 'N/A'} {dealer.MAILING_ADDRESS?.ZIP_CODE || 'N/A'}</p>
          <hr className="thin-divider" />
          <p>
            <strong>Phone:</strong>{' '}
            <a href={`tel:${dealer.VOICE_PHONE}`} className="phone-link">
              {formatPhoneNumber(dealer.VOICE_PHONE)}
            </a>
          </p>
          {dealer.EMAIL && (
            <p>
              <strong>Email:</strong>{" "}
              <a href={`mailto:${dealer.EMAIL}`} className="contact-link">
                {dealer.EMAIL}
              </a>
            </p>
          )}
          {dealer.WEBSITE && (
            <p>
              <strong>Website:</strong>{" "}
              <a href={dealer.WEBSITE} target="_blank" rel="noopener noreferrer" className="contact-link">
                {dealer.WEBSITE.replace(/^https?:\/\//, '')}
              </a>
            </p>
          )}
          <p>
            <strong>Local Time:</strong>{' '}
            {localTime ? localTime : 'Fetching local time...'}
          </p>
        </div>
        <div className="details-box">
          <h2>FFL Definition</h2>
          <p><strong>IRS Region:</strong> {dealer.LIC_REGN || 'N/A'} - {irsRegionDescription}</p>
          <p><strong>District:</strong> {dealer.LIC_DIST || 'N/A'}</p>
          <p><strong>FIPS Code:</strong> {dealer.LIC_CNTY || 'N/A'}</p>
          <p><strong>Type:</strong> {dealer.LIC_TYPE || 'N/A'} - {typeDescription || 'Loading...'}</p>
          <p><strong>Expires:</strong>&nbsp;<span className={`claim-date ${getExpirationClass(expirationDate)}`}>
            {formatDate(expirationDate)}&nbsp;
            ({getDaysLeft(expirationDate)} days left)
          </span>
          </p>
          <p><strong>Sequence Number:</strong> {dealer.LIC_SEQN || 'N/A'}</p>

          <button className="ez-check-button"
            onClick={async () => {
              try {
                // Create the form programmatically
                const form = document.createElement('form');
                form.action = 'https://fflezcheck.atf.gov/FFLEzCheck/fflSearch'; // Target URL
                form.method = 'POST';
                form.target = '_blank'; // Open in a new window

                // Add form data (hidden inputs)
                const data = {
                  licsRegn: dealer.LIC_REGN,
                  licsDis: dealer.LIC_DIST, // Replace with the first 3 digits of the FFL
                  licsSeq: dealer.LIC_SEQN, // Replace with the last 5 digits of the FFL
                };

                for (const key in data) {
                  const input = document.createElement('input');
                  input.type = 'hidden';
                  input.name = key;
                  input.value = data[key];
                  form.appendChild(input);
                }

                // Append the form to the body
                document.body.appendChild(form);

                // Submit the form
                form.submit();
              } catch (error) {
                // console.error('Error submitting FFL:', error);
              }
            }}
          >
            eZ Check
          </button>

          <p className="ez-check-description">
            FFL eZ Check is a tool provided by the Bureau of Alcohol, Tobacco, Firearms, and Explosives (ATF) for easily checking whether or not an FFL license is valid and verifying the shipping information.
          </p>
        </div>
      </div>
      <div className="map-panel">
        <h2>Location and Nearby FFLs</h2>
        {position ? (
          <MapContainer
            center={position}
            zoom={12}
            className="leaflet-container map-content" // Apply custom styles to the map
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution=''
            />
            {/* Display dealer with unique marker */}
            {position && (
              <Marker position={position} icon={displayDealerIcon} ref={markerRef}>
                <Popup>
                  <strong>{dealer.BUSINESS_NAME || dealer.LICENSE_NAME}</strong><br />
                  {PREMISE_ADDRESS?.STREET || 'N/A'}, {PREMISE_ADDRESS?.CITY || 'N/A'}, {PREMISE_ADDRESS?.STATE || 'N/A'} {PREMISE_ADDRESS?.ZIP_CODE || 'N/A'}<br />
                  <a href={`tel:${dealer.VOICE_PHONE}`}>
                    {formatPhoneNumber(dealer.VOICE_PHONE)}
                  </a>
                </Popup>
              </Marker>
            )}

            {/* Other dealers with default marker */}
            {otherLocations
              .filter((loc) => loc.latitude !== undefined && loc.longitude !== undefined)
              .map((loc) => (
                <Marker key={loc.id} position={[loc.latitude, loc.longitude]} icon={nearbyDealerIcon}>
                  <Popup>
                    <strong>{loc.name}</strong><br />
                    {loc.address?.STREET || 'N/A'}, {loc.address?.CITY || 'N/A'}
                  </Popup>
                </Marker>
              ))}
          </MapContainer>
        ) : (
          <p>Fetching location...</p>
        )}
      </div>
      <div className="zipcode-dealers-container">
        <h2>Other FFLs in Same ZIPCODE</h2>
        {otherFFLs.length > 0 ? (
          <div className="zipcode-dealers-list">
            {otherFFLs.map((ffl) => (
              <div
                key={ffl._id}
                className="zipcode-dealer-card"
                onClick={() => {
                  navigate(`/dealer/${ffl._id}`, {
                    state: { origin: `/dealer/${id}` }, // Pass the current dealer ID as origin
                  });
                  window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to the top of the page
                }}
                style={{ cursor: 'pointer' }} // Indicate clickability
              >
                <h3>{ffl.BUSINESS_NAME || ffl.LICENSE_NAME || 'N/A'}</h3>
                <p>
                  {ffl.PREMISE_ADDRESS?.STREET || 'N/A'}, {ffl.PREMISE_ADDRESS?.CITY || 'N/A'}, {ffl.PREMISE_ADDRESS?.STATE || 'N/A'} {ffl.PREMISE_ADDRESS?.ZIP_CODE || 'N/A'}
                </p>
                <p>
                  <a
                    href={`tel:${ffl.VOICE_PHONE}`}
                    onClick={(e) => e.stopPropagation()} // Prevent card click
                  >
                    {formatPhoneNumber(ffl.VOICE_PHONE)}
                  </a>
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p>No other FFLs found in this ZIP code.</p>
        )}
      </div>
    </div>
  );
};

export default DealerDetails;